/**
 * Production Order Form Page
 *
 * Create or edit a Production Order
 * Features:
 * - Select BOM
 * - Set planned quantity
 * - Assign work center
 * - Set priority and dates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  Search,
  Factory,
  FileText,
  Package,
  PackagePlus,
  Calendar,
  Scissors,
} from 'lucide-react';
import Layout from '../components/Layout';
import productionService from '../services/productionService';
import bomService from '../services/bomService';

function ProductionFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [bomId, setBomId] = useState('');
  const [selectedBom, setSelectedBom] = useState(null);
  const [plannedQuantity, setPlannedQuantity] = useState(1);
  const [priority, setPriority] = useState('normal');
  const [workCenterId, setWorkCenterId] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Source info (from sales order)
  const [sourceType, setSourceType] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceLineItemId, setSourceLineItemId] = useState('');

  // BOM search
  const [bomSearchOpen, setBomSearchOpen] = useState(false);
  const [bomSearchTerm, setBomSearchTerm] = useState('');
  const [bomSearchResults, setBomSearchResults] = useState([]);
  const [bomSearchLoading, setBomSearchLoading] = useState(false);

  // Work centers
  const [workCenters, setWorkCenters] = useState([]);

  // Leftover suggestions
  const [leftoverSuggestions, setLeftoverSuggestions] = useState([]);
  const [leftoverLoading, setLeftoverLoading] = useState(false);

  // Fetch leftover suggestions when BOM or quantity changes
  const fetchLeftoverSuggestions = useCallback(async () => {
    if (!bomId || plannedQuantity <= 0) {
      setLeftoverSuggestions([]);
      return;
    }

    try {
      setLeftoverLoading(true);
      const response = await productionService.suggestLeftovers(bomId, plannedQuantity);
      
      if (response.success && response.suggestions && response.suggestions.length > 0) {
        setLeftoverSuggestions(response.suggestions);
      } else {
        setLeftoverSuggestions([]);
      }
    } catch (err) {
      console.error('Failed to fetch leftover suggestions:', err);
      setLeftoverSuggestions([]);
    } finally {
      setLeftoverLoading(false);
    }
  }, [bomId, plannedQuantity]);

  // Load work centers
  const loadWorkCenters = useCallback(async () => {
    try {
      const response = await productionService.getWorkCenters({ status: 'active' });
      setWorkCenters(response.items || response || []);
    } catch (err) {
      console.error('Failed to load work centers:', err);
    }
  }, []);

  useEffect(() => {
    loadWorkCenters();
  }, [loadWorkCenters]);

  // Load order for editing
  const loadOrder = useCallback(async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      setError(null);

      const order = await productionService.getProductionOrder(id);
      setBomId(order.bom_id);
      setPlannedQuantity(order.planned_quantity);
      setPriority(order.priority || 'normal');
      setWorkCenterId(order.work_center_id || '');
      setPlannedStartDate(order.planned_start_date || '');
      setPlannedEndDate(order.planned_end_date || '');
      setNotes(order.notes || '');
      setSourceType(order.source_type || '');
      setSourceId(order.source_id || '');
      setSourceLineItemId(order.source_line_item_id || '');

      // Load BOM details
      if (order.bom_id) {
        try {
          const bom = await bomService.getBom(order.bom_id);
          setSelectedBom(bom);
        } catch (err) {
          console.error('Failed to load BOM:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load order:', err);
      setError('Failed to load production order. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Check for pre-filled BOM from URL params
  useEffect(() => {
    const bomIdParam = searchParams.get('bom_id');
    const qtyParam = searchParams.get('quantity');
    const sourceParam = searchParams.get('source_type');
    const sourceIdParam = searchParams.get('source_id');
    const lineItemParam = searchParams.get('line_item_id');

    if (bomIdParam && !isEditMode) {
      // Load the BOM
      bomService.getBom(bomIdParam).then((bom) => {
        setBomId(bomIdParam);
        setSelectedBom(bom);
      }).catch((err) => {
        console.error('Failed to load BOM from URL:', err);
      });
    }

    if (qtyParam) {
      setPlannedQuantity(Number(qtyParam));
    }

    if (sourceParam) {
      setSourceType(sourceParam);
    }

    if (sourceIdParam) {
      setSourceId(sourceIdParam);
    }

    if (lineItemParam) {
      setSourceLineItemId(lineItemParam);
    }
  }, [searchParams, isEditMode]);

  // Fetch leftover suggestions when BOM or quantity changes (not in edit mode)
  useEffect(() => {
    if (!isEditMode && bomId && plannedQuantity > 0) {
      const timer = setTimeout(() => {
        fetchLeftoverSuggestions();
      }, 500); // Debounce 500ms
      return () => clearTimeout(timer);
    }
  }, [bomId, plannedQuantity, isEditMode, fetchLeftoverSuggestions]);

  // Search BOMs
  const searchBoms = useCallback(async () => {
    try {
      setBomSearchLoading(true);
      const response = await bomService.getBoms({
        search: bomSearchTerm,
        status: 'active',
        page_size: 20,
      });
      setBomSearchResults(response.items || []);
    } catch (err) {
      console.error('Failed to search BOMs:', err);
    } finally {
      setBomSearchLoading(false);
    }
  }, [bomSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bomSearchOpen) {
        searchBoms();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bomSearchTerm, bomSearchOpen, searchBoms]);

  const handleBack = () => {
    if (isEditMode) {
      navigate(`/production/${id}`);
    } else {
      navigate('/production');
    }
  };

  const selectBom = async (bom) => {
    setBomId(bom._id);
    setSelectedBom(bom);
    setBomSearchOpen(false);
  };

  const handleSave = async () => {
    // Validate
    if (!bomId) {
      setError('请选择BOM。');
      return;
    }

    if (plannedQuantity <= 0) {
      setError('计划数量必须大于0。');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const orderData = {
        bom_id: bomId,
        planned_quantity: Number(plannedQuantity),
        priority,
        work_center_id: workCenterId || null,
        planned_start_date: plannedStartDate || null,
        planned_end_date: plannedEndDate || null,
        notes,
      };

      // Add source info if creating from sales order
      if (!isEditMode && sourceType) {
        orderData.source_type = sourceType;
        orderData.source_id = sourceId;
        orderData.source_line_item_id = sourceLineItemId;
      }

      if (isEditMode) {
        await productionService.updateProductionOrder(id, orderData);
        navigate(`/production/${id}`);
      } else {
        const newOrder = await productionService.createProductionOrder(orderData);
        navigate(`/production/${newOrder._id}`);
      }
    } catch (err) {
      console.error('Failed to save order:', err);
      setError(`Failed to save production order: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Calculate material requirements preview
  const materialRequirements = selectedBom?.materials?.map((m) => ({
    ...m,
    required_quantity: (m.quantity_per * plannedQuantity * (1 + (m.scrap_rate || 0))).toFixed(3),
  })) || [];

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditMode ? '编辑生产工单' : '新建生产工单'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode
                  ? '更新生产工单详情'
                  : '创建新的生产工单'}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            保存工单
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Source Info (if from sales order) */}
        {sourceType && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Creating production order from {sourceType}: {sourceId}
            </AlertDescription>
          </Alert>
        )}

        {/* BOM Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              物料清单
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>选择BOM</Label>
              <div className="flex gap-2">
                <Input
                  value={
                    selectedBom
                      ? `${selectedBom.bom_id} - ${selectedBom.finished_product_name}`
                      : ''
                  }
                  placeholder="选择BOM..."
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={() => setBomSearchOpen(true)}
                  disabled={isEditMode}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {selectedBom && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">产品代码</p>
                  <p className="font-mono">{selectedBom.finished_product_code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">产品名称</p>
                  <p>{selectedBom.finished_product_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">版本</p>
                  <p>V{selectedBom.version}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">物料数</p>
                  <p>{selectedBom.materials?.length || 0}项</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              工单详情
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>计划数量</Label>
                <Input
                  type="number"
                  value={plannedQuantity}
                  onChange={(e) => setPlannedQuantity(Number(e.target.value))}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>优先级</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">低</SelectItem>
                    <SelectItem value="normal">普通</SelectItem>
                    <SelectItem value="high">高</SelectItem>
                    <SelectItem value="urgent">紧急</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>工作中心</Label>
                <Select value={workCenterId || 'none'} onValueChange={(val) => setWorkCenterId(val === 'none' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="选择工作中心..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">未分配</SelectItem>
                    {workCenters.map((wc) => (
                      <SelectItem key={wc._id} value={wc._id}>
                        {wc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  计划开始日期
                </Label>
                <Input
                  type="date"
                  value={plannedStartDate}
                  onChange={(e) => setPlannedStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  计划完成日期
                </Label>
                <Input
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Material Requirements Preview */}
        {selectedBom && materialRequirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                物料需求预览
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料代码</TableHead>
                    <TableHead>物料名称</TableHead>
                    <TableHead className="text-right">单位用量</TableHead>
                    <TableHead className="text-right">损耗率</TableHead>
                    <TableHead className="text-right">需求量</TableHead>
                    <TableHead>单位</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialRequirements.map((material, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono">
                        {material.material_product_code}
                      </TableCell>
                      <TableCell>{material.material_name}</TableCell>
                      <TableCell className="text-right">{material.quantity_per}</TableCell>
                      <TableCell className="text-right">
                        {((material.scrap_rate || 0) * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {material.required_quantity}
                      </TableCell>
                      <TableCell>{material.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Leftover Suggestions */}
        {!isEditMode && leftoverSuggestions.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Scissors className="h-5 w-5" />
                余料建议
                <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800">
                  {leftoverSuggestions.length} 个物料有余料
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4 bg-amber-100 border-amber-300">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  检测到可用余料，建议优先使用以节省成本。点击展开查看详情。
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                {leftoverSuggestions.map((suggestion, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-amber-900">
                          {suggestion.material_product_code}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          直径: {suggestion.tip_diameter}mm · 需求量: {suggestion.total_required.toFixed(2)} {suggestion.unit}
                        </p>
                      </div>
                      <Badge variant="success" className="bg-green-100 text-green-800">
                        可用 {suggestion.total_leftover_quantity} 根
                      </Badge>
                    </div>

                    {/* Available leftovers list */}
                    <div className="mt-2 pl-3 border-l-2 border-amber-300">
                      <p className="text-xs text-muted-foreground mb-1">可用余料:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {suggestion.available_leftovers.slice(0, 3).map((lo, loIdx) => (
                          <div key={loIdx} className="flex items-center gap-2 text-sm">
                            <span className="font-mono bg-amber-100 px-2 py-0.5 rounded text-amber-800">
                              {lo.length}mm
                            </span>
                            <span className="text-muted-foreground">
                              × {lo.quantity} 根
                            </span>
                            <span className="text-xs text-muted-foreground">
                              @{lo.location}
                            </span>
                          </div>
                        ))}
                        {suggestion.available_leftovers.length > 3 && (
                          <p className="text-xs text-muted-foreground col-span-2">
                            ... 还有 {suggestion.available_leftovers.length - 3} 条余料
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-amber-700">
                <PackagePlus className="h-4 w-4" />
                <span>
                  使用余料可减少新物料采购，建议在发放物料时优先选择余料。
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading indicator for leftover suggestions */}
        {!isEditMode && leftoverLoading && (
          <Card className="border-dashed">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>正在搜索可用余料...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>备注</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="可选生产工单备注..."
              rows={4}
            />
          </CardContent>
        </Card>
      </div>

      {/* BOM Search Dialog */}
      <Dialog open={bomSearchOpen} onOpenChange={setBomSearchOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>选择物料清单</DialogTitle>
            <DialogDescription>
              搜索并选择有效的BOM用于生产。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按BOM编号、产品代码或名称搜索..."
                value={bomSearchTerm}
                onChange={(e) => setBomSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-80 overflow-y-auto">
              {bomSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : bomSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  未找到有效的BOM。
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>BOM编号</TableHead>
                      <TableHead>产品</TableHead>
                      <TableHead>版本</TableHead>
                      <TableHead>物料数</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bomSearchResults.map((bom) => (
                      <TableRow
                        key={bom._id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => selectBom(bom)}
                      >
                        <TableCell className="font-mono">{bom.bom_id}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{bom.finished_product_code}</div>
                            <div className="text-sm text-muted-foreground">
                              {bom.finished_product_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>V{bom.version}</TableCell>
                        <TableCell>{bom.materials?.length || 0}项</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBomSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default ProductionFormPage;
